/**
 * Root observability span (client). Runs before every other plugin (`enforce: 'pre'`
 * + `000.` filename) so that:
 *   1. the app-scoped ambient `frogger` logger carries the base context (tenant,
 *      env, version, renderMode) for EVERY subsequent `frogger.*` call anywhere in
 *      the app — this is the app-session root span; and
 *   2. we emit a single bootstrap marker (session/deploy tracking) and surface any
 *      redeploy self-heal that `app-boot.js` performed before the bundle even loaded.
 *
 * Base context is derived synchronously from the baked customer config, so it is
 * available at this earliest point in a static/SPA build. Session identity (`user`)
 * is added later, once auth resolves, in app.vue.
 */
/**
 * Decide whether a boot-time self-heal should be reported now. Returns the report
 * context when the `app-boot-heal-ts` marker is present and has not already been
 * reported (`app-boot-heal-reported` de-dupe), or null otherwise — so a single
 * heal surfaces exactly once, and a later heal (new marker) surfaces again.
 */
export function computeHealRecovery(
	healed: string | null,
	reported: string | null,
	now: number,
): { healTs: number; ageMs: number } | null {
	if (!healed || healed === reported) return null;
	return { healTs: Number(healed), ageMs: now - Number(healed) };
}

export default defineNuxtPlugin({
	name: 'frogger-root',
	enforce: 'pre',
	setup(nuxtApp) {
		const base = useFroggerBaseContext();

		// Every ambient frogger.* call in this app now inherits the tenant/env/version.
		// This runs at `pre` so the base context is stamped before any other plugin logs.
		frogger.setContext(base);

		// Emitting must wait for frogger's own `log-queue.client` plugin to provide
		// `$logQueue` — that plugin runs in the default group, AFTER this `pre` plugin,
		// so logging here directly throws `Cannot read properties of undefined
		// (reading 'enqueueLog')`. `app:mounted` fires once the queue is in place.
		nuxtApp.hook('app:mounted', () => {
			// One marker per hard load — tracks sessions and makes a redeploy visible.
			frogger.info('app bootstrap: root trace established', base);

			// app-boot.js reloads the page when a cached HTML references /_nuxt chunk hashes
			// that 404 after a redeploy, leaving a sessionStorage timestamp. Surface that
			// self-heal now (the boot script itself runs before any logger exists) so
			// redeploy breakage is observable rather than silent.
			try {
				const HEAL_KEY = 'app-boot-heal-ts';
				const REPORTED_KEY = 'app-boot-heal-reported';
				const healed = sessionStorage.getItem(HEAL_KEY);
				const recovery = computeHealRecovery(
					healed,
					sessionStorage.getItem(REPORTED_KEY),
					Date.now(),
				);
				if (recovery) {
					sessionStorage.setItem(REPORTED_KEY, healed as string);
					frogger.warn('recovered from redeploy chunk mismatch', recovery);
				}
			} catch {
				// sessionStorage can throw (private mode / disabled storage) — non-fatal.
			}
		});
	},
});
